import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import ActivityCard from "@/components/ActivityCard";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { ArrowLeft, Users, Medal, CheckSquareOffset, Calendar } from "@phosphor-icons/react";

/**
 * Profile → My Activities — three tabs that mirror the native mobile app:
 *   • TILMELDT / JOINED      — sessions the user joined (not hosted)
 *   • MINE SESSIONER / MY SESSIONS — sessions the user hosts
 *   • AFSLUTTET / COMPLETED  — finished sessions (hosted or joined)
 *
 * Counts are loaded for all three tabs up-front so the tab pills show the
 * same `0  1  6` style numbers as the mobile app screenshot.
 */
const TABS = [
  { key: "joined",    icon: Users,              accent: "#FFFFFF" },
  { key: "hosted",    icon: Medal,              accent: "#FFCC00" },
  { key: "completed", icon: CheckSquareOffset,  accent: "#34C759" },
];

export default function MyActivities() {
  const { t, lang } = useI18n();
  const [active, setActive] = useState("joined");
  const [data, setData] = useState({ joined: null, hosted: null, completed: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [j, h, c] = await Promise.all([
          api.get("/activities/mine?status=joined").catch(() => ({ data: [] })),
          api.get("/activities/mine?status=hosted").catch(() => ({ data: [] })),
          api.get("/activities/mine?status=completed").catch(() => ({ data: [] })),
        ]);
        setData({
          joined: j.data || [],
          hosted: h.data || [],
          completed: c.data || [],
        });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const items = data[active] || [];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <AppHeader />
      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-center gap-4">
          <Link
            to="/profile"
            data-testid="my-activities-back"
            className="kinetic-btn w-10 h-10 flex items-center justify-center border border-[#262626] hover:border-white/40 bg-[#141414]"
            aria-label={lang === "da" ? "Tilbage" : "Back"}
          >
            <ArrowLeft weight="bold" size={18} />
          </Link>
          <div>
            <div className="label-overline text-[#007AFF]">{t("profile.kicker")}</div>
            <h1 className="font-display text-5xl tracking-tight mt-2">
              {t("my_activities.title").toUpperCase()}
            </h1>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-3 gap-3 sm:gap-4" data-testid="my-activities-tabs">
          {TABS.map(({ key, icon: Icon, accent }) => {
            const isActive = active === key;
            const count = data[key]?.length ?? 0;
            return (
              <button
                key={key}
                data-testid={`my-activities-tab-${key}`}
                onClick={() => setActive(key)}
                className={`kinetic-btn p-5 sm:p-6 border text-left transition-colors ${
                  isActive
                    ? "bg-white border-white text-black"
                    : "border-[#262626] bg-[#141414] hover:border-white/40 text-white"
                }`}
              >
                <div className="flex items-center justify-between">
                  <Icon weight="duotone" size={22} color={isActive ? "#0A0A0A" : accent} />
                  <span
                    className="font-display text-2xl leading-none"
                    style={{ color: isActive ? "#0A0A0A" : "#FFFFFF" }}
                  >
                    {count}
                  </span>
                </div>
                <div
                  className="label-overline mt-3"
                  style={{ color: isActive ? "#0A0A0A" : "rgba(255,255,255,0.7)" }}
                >
                  {t(`my_activities.tab.${key}`)}
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-8" data-testid={`my-activities-grid-${active}`}>
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {["sk1", "sk2", "sk3"].map((k) => (
                <div key={k} className="tf-card h-44 animate-pulse" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <EmptyState lang={lang} active={active} />
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((a) => (
                <ActivityCard key={a.activity_id} activity={a} />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function EmptyState({ lang, active }) {
  // Match the mobile app's "Ingen sessioner i denne kategori endnu." copy.
  const headline = lang === "da"
    ? "Ingen sessioner i denne kategori endnu."
    : "No sessions in this category yet.";
  const ctaLabel = active === "completed"
    ? (lang === "da" ? "Find en session" : "Find a session")
    : (lang === "da" ? "Opret en session" : "Create a session");
  const ctaTo = active === "completed" ? "/explore" : "/create";
  return (
    <div
      className="border border-dashed border-[#262626] bg-[#141414] py-16 px-6 flex flex-col items-center text-center"
      data-testid="my-activities-empty"
    >
      <div className="w-14 h-14 border border-[#262626] flex items-center justify-center text-zinc-500">
        <Calendar weight="duotone" size={28} />
      </div>
      <p className="mt-5 text-sm text-zinc-400">{headline}</p>
      <Link
        to={ctaTo}
        data-testid="my-activities-empty-cta"
        className="kinetic-btn mt-6 bg-[#007AFF] hover:bg-[#0a66d0] text-white px-5 py-3 text-xs uppercase tracking-widest font-bold"
      >
        {ctaLabel}
      </Link>
    </div>
  );
}
